import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import { clearRememberedAuthRedirect, getRememberedAuthRedirect } from '../utils/authRedirect';

// Handles the redirect from the backend after a successful Google OAuth flow.
// The backend encodes the bearer token in the URL fragment:
//   /auth/callback#token=whynotlog-...
// Fragments are never sent to the server, so the token stays client-side only.
const OAuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const handled = useRef(false);

  useEffect(() => {
    // Guard against StrictMode double-invocation.
    if (handled.current) return;
    handled.current = true;

    const hash = window.location.hash;
    const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
    const token = params.get('token');
    const userId = params.get('user_id');

    if (!token || !userId) {
      navigate('/login?error=oauth_failed', { replace: true });
      return;
    }

    localStorage.setItem('token', token);
    api.setToken(token);

    // Fetch the user profile so AuthContext is fully populated.
    api.getSelfUser()
      .then((user) => {
        setSession(user, token);
        const destination = getRememberedAuthRedirect() ?? '/';
        clearRememberedAuthRedirect();
        navigate(destination, { replace: true });
      })
      .catch(() => {
        // Token was invalid or the profile fetch failed — clear and redirect.
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        api.setToken(null);
        navigate('/login?error=oauth_failed', { replace: true });
      });
  }, [navigate, setSession]);

  return (
    <div className="auth-container">
      <div className="auth-box" style={{ textAlign: 'center' }}>
        <p>Signing you in...</p>
      </div>
    </div>
  );
};

export default OAuthCallback;
